import React from 'react';
import PropTypes from 'prop-types';
import FortressPower from './Batteries/fortress';
import LGChem from './Batteries/lgchem';
import GeneracBattery from './Batteries/generacStorage';
import BatteryUses from './BatteryUses';
import BatteryPix from './BatteryPix';
import BatteryPrice from './BatteryPrice';
import { Title, Subtitle } from '../../../../Home/styles';
import Fortress18 from './EnergyStoragePics/Fortress18';
import Fortress10 from './EnergyStoragePics/Fortress10';
import Fortress5 from './EnergyStoragePics/Fortress5';
import PWRCellPic from './EnergyStoragePics/PWRCell';
import LGChemBatteryPic from './EnergyStoragePics/LGChemBattery';

import { CopyContainer, Container } from './styles';

import { formattedDollar, formattedNumber } from '../../../../../utils/formatterHelpers';
import '../../../../../grid.css';
import './ESSPoints.css';


const battery101Link = (
    <a
        href="https://www.youtube.com/watch?v=kXhy_Alp_ys"
        rel="noopener noreferrer"
        target="_blank"
    >
        Learn How Batteries Work
    </a>);

class ESSPoints extends React.Component {
    static propTypes = {
        annualResults: PropTypes.object.isRequired,
        setSelectedFinancing: PropTypes.func.isRequired,
        essPrice: PropTypes.number.isRequired,
        batteryType: PropTypes.string.isRequired,
        loanProvider: PropTypes.string.isRequired,
        resiToggle: PropTypes.bool.isRequired,
        showCAP: PropTypes.bool.isRequired,
        showLoan: PropTypes.bool.isRequired,
        mosaicFee: PropTypes.number.isRequired,
        companyName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        projectState: PropTypes.number.isRequired,
        systemSize: PropTypes.number.isRequired,
        mosaicTerm: PropTypes.number.isRequired,
    };


    handleSelectPPA() {
        const { setSelectedFinancing } = this.props;
        setSelectedFinancing(1);
    }

    render() {
        const {
            annualResults,
            essPrice,
            batteryType,
            resiToggle,
            showLoan,
            loanProvider,
            mosaicFee,
            companyName,
            lastName,
            firstName,
            showCAP,
            projectState,
            systemSize,
            mosaicTerm,
        } = this.props;

        let essPriceNetITC = 0;
        if (essPrice) {
            essPriceNetITC = essPrice - annualResults[1].ITCCashESS;
        }

        let batteryName = 'Energy Storage System';
        if (batteryType === 'fortress5') {
            batteryName = 'Fortress Power 5kWh';
        } else if (batteryType === 'fortress10') {
            batteryName = 'Fortress Power 10kWh';
        } else if (batteryType === 'fortress18') {
            batteryName = 'Fortress Power 18.5kWh';
        } else if (batteryType === 'lgchem') {
            batteryName = 'LG Chem (Batteries)';
        } else if (batteryType === 'generacbattery') {
            batteryName = 'Generac PWRCell Battery';
        }

        let batteryText = null;
        if (batteryType === 'fortress5' || batteryType === 'fortress10' || batteryType === 'fortress18') {
            batteryText = <FortressPower batteryType={batteryType} batteryName={batteryName} />;
        } else if (batteryType === 'lgchem') {
            batteryText = <LGChem />;
        } else if (batteryType === 'generacbattery') {
            batteryText = <GeneracBattery />;
        }
        let batteryPicture = null;
        if (batteryType === 'fortress5') {
            batteryPicture = <Fortress5 />;
        }
        if (batteryType === 'fortress10') {
            batteryPicture = <Fortress10 />;
        }
        if (batteryType === 'fortress18') {
            batteryPicture = <Fortress18 />;
        }
        if (batteryType === 'lgchem') {
            batteryPicture = <LGChemBatteryPic />;
        }
        if (batteryType === 'generacbattery') {
            batteryPicture = <PWRCellPic />;
        }

        let smartTerm = 10;
        if (systemSize >= 25000) {
            smartTerm = 20;
        }

        let smartESSAdder = 0;
        if (projectState === 2) {
            smartESSAdder = (0.046 * annualResults[1].systemProduction / 12);
        }

        const mosaicAdder =
        formattedNumber((
            annualResults[1].annualHomeLoanPaymentESS - annualResults[1].annualHomeLoanPayment) / 12 - smartESSAdder);

        let mosaicAdderText = null;

        if (showLoan && loanProvider === 'mosaic') {
            mosaicAdderText = `Adds $${mosaicAdder}/month`;
        }
        let loanName = null;

        if (showLoan && loanProvider === 'mosaic') {
            loanName = 'Mosaic Financing Adder';
        }
        if (showLoan && loanProvider === 'custom') {
            loanName = 'Loan';
        }

        let essCashPrice = `${formattedDollar(essPrice)}`;
        if (showLoan && loanProvider === 'mosaic') {
            essCashPrice = formattedDollar(essPrice * mosaicFee);
        }

        const terms = {
            annualResults,
            essPrice,
            batteryType,
            resiToggle,
            showLoan,
            loanProvider,
            mosaicFee,
            companyName,
            firstName,
            lastName,
            essPriceNetITC,
            mosaicAdderText,
            loanName,
            showCAP,
            essCashPrice,
            projectState,
            mosaicTerm,
            smartTerm,
        };

        let name = companyName;
        if (resiToggle) {
            name = `${firstName} ${lastName}`;
        }
        return (
            <div>
                <section className="heroContainer">
                    <div className="BG" />
                    <div className="BGImg" />
                    <div className="Content">
                        <Title>
                            Capture the Sun with Batteries
                        </Title>
                        <Subtitle>
                            Use your energy when you want, and need it most.
                        </Subtitle>
                    </div>
                </section>
                <Container>
                    <CopyContainer>
                        <h1>
                            {name}, many customers reinvest some of the savings from their PV solar-generating system into an energy storage system (ESS), to improve safety, peace-of-mind, quality of life, independence, savings, and add more value.
                        </h1>
                        <p>
                            Solvana recommends combining PV solar-generating systems with the safety and security of high-performance {batteryName} Power batteries and Sol-Ark hybrid inverters. An ESS adds fresh flexibility to homes and businesses by supplying backup power when the electrical utility grid goes down, while simultaneously adding significant value in several other ways. When the electrical utility grid fails, if you have an ESS, you will be glad you can power your essential electrical loads. When combined with a PV solar-generating system, an ESS is also eligible for a Federal Investment Tax Credit and other available Local Incentives.
                        </p>
                    </CopyContainer>

                    <BatteryUses terms={terms} />
                    <BatteryPix
                        terms={terms}
                        batteryText={batteryText}
                        batteryName={batteryName}
                        batteryPicture={batteryPicture}
                    />
                    <BatteryPrice
                        terms={terms}
                        batteryText={batteryText}
                        batteryName={batteryName}
                        batteryPicture={batteryPicture}
                    />
                    <div className="row">
                        {battery101Link}
                    </div>
                </Container>
            </div>
        );
    }
}

export default ESSPoints;



// WEBPACK FOOTER //
// ./src/containers/Simulator/TabBar/Results/ESSPoints/ESSPoints.js